import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Form } from 'antd';
import { v4 as uuidv4 } from 'uuid';

import { useCreateRosaActionPlan } from '../../../hooks/useCreateRosaActionPlan';
import { useUpdateRosaActionPlan } from '../../../hooks/useUpdateRosaActionPlan';
import { useDeleteRosaActionPlan } from '../../../hooks/useDeleteRosaActionPlan';
import { ModalDelete } from './EditActionPlan/ModalDelete';
import { EditActionPlan } from './EditActionPlan';
import { useRosaContext } from '../../../context';
import { useSectionActionPlan } from './context';
import { NewActionPlan } from './NewActionPlan';
import { Task } from './Tasks/types';
import { BoardType } from '@/types';
import { Footer } from './Footer';
import { Tasks } from './Tasks';
import * as F from './Fields';
import * as S from './styles';

const { useFormInstance, useWatch } = Form;

export function Content() {
	const form = useFormInstance();
	const { form_field_name, category } = useSectionActionPlan();
	const { organization_id, company_id, actionPlans, report_id } = useRosaContext();

	const initialTasks = actionPlans[category.name]?.tasks as Task[];
	const initialActionPlanId = actionPlans[category.name]?.action_plan_id;

	const [isFilling, setIsFilling] = useState<boolean>(false);
	const [tasks, setTasks] = useState<Task[]>(initialTasks ?? []);
	const [localActionPlanId, setLocalActionPlanId] = useState<string | undefined>(initialActionPlanId);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

	const watchedActionPlanId = useWatch([...form_field_name, 'action_plan_id']);
	const actionPlanId = watchedActionPlanId ?? localActionPlanId;

	const {
		isLoading: isCreatingActionPlan,
		mutateAsync: createRosaActionPlan,
		isError: hasCreateActionPlanError
	} = useCreateRosaActionPlan();

	const {
		isLoading: isUpdatingActionPlan,
		mutateAsync: updateRosaActionPlan,
		isError: hasUpdateActionPlanError
	} = useUpdateRosaActionPlan();

	const {
		isLoading: isDeletingActionPlan,
		mutateAsync: deleteRosaActionPlan,
		isError: hasDeleteActionPlanError
	} = useDeleteRosaActionPlan();

	const isLoading = isCreatingActionPlan || isUpdatingActionPlan || isDeletingActionPlan;
	const hasError = hasCreateActionPlanError || hasUpdateActionPlanError || hasDeleteActionPlanError;

	function handleDeleteModalToggle() {
		setIsDeleteModalOpen(!isDeleteModalOpen);
	}

	async function handleSubmitActionPlan(): Promise<void> {
		if (actionPlanId && isFilling) {
			return await update();
		}

		await create();
	}

	async function create(): Promise<void> {
		const formValues = form.getFieldValue(form_field_name);

		const mappedTask = tasks.map(({ description, type }) => {
			return { type, description };
		});

		const createdRosaActionPlan = await createRosaActionPlan({
			organization_id,
			company_id,
			report_id,
			category_id: category.id,
			action_plan: {
				title: formValues.title,
				board: BoardType.TO_DO,
				deadline: formValues.deadline,
				description: formValues.description,
				responsible_user_id: formValues.responsible_user_id,
				tasks: mappedTask
			}
		});

		if (createdRosaActionPlan.id) {
			form.setFieldValue([...form_field_name, 'action_plan_id'], createdRosaActionPlan.action_plan_id);
			setLocalActionPlanId(createdRosaActionPlan.action_plan_id);
			setIsFilling(false);
		}
	}

	async function update(): Promise<void> {
		const formValues = form.getFieldValue(form_field_name);

		const updatedRosaActionPlan = await updateRosaActionPlan({
			organization_id,
			company_id,
			id: actionPlanId,
			board: BoardType.TO_DO,
			title: formValues.title,
			deadline: formValues.deadline,
			description: formValues.description,
			responsible_user_id: formValues.responsible_user_id,
			tasks
		});

		if (updatedRosaActionPlan.data.id) {
			form.setFieldValue([...form_field_name, 'action_plan_id'], updatedRosaActionPlan.data.id);
			setLocalActionPlanId(updatedRosaActionPlan.data.id);
			setIsFilling(false);
		}
	}

	async function deletePlan(): Promise<void> {
		const deletedActionPlan = await deleteRosaActionPlan({
			organization_id,
			company_id,
			action_plan_id: actionPlanId,
			category_id: category.id,
			report_id
		});

		if (deletedActionPlan.data.id) {
			form.setFieldValue(form_field_name, undefined);
			form.setFieldValue([...form_field_name, 'action_plan_id'], undefined);
			setLocalActionPlanId(undefined);
		}

		setIsDeleteModalOpen(false);
	}

	function handleAddTask(): void {
		const taskFormValues = form.getFieldValue([...form_field_name, 'task']);

		const task: Task = {
			id: uuidv4(),
			type: taskFormValues.condition,
			description: taskFormValues.title
		};

		setTasks((previousTasks) => [...previousTasks, task]);

		form.resetFields([[...form_field_name, 'task']]);
	}

	function handleRemoveTask(taskIndex: number): void {
		const tasksCopy = [...tasks];
		tasksCopy.splice(taskIndex, 1);
		setTasks(tasksCopy);
	}

	function handleToggle(): void {
		setIsFilling(!isFilling);
	}

	if (actionPlanId && !isFilling && !hasError) {
		return (
			<Col span={24}>
				<Row justify="center">
					<EditActionPlan
						isLoading={isLoading}
						onClick={handleToggle}
						onClickDelete={handleDeleteModalToggle}
					/>
					<ModalDelete
						description={I18n.get('Are you sure you want to delete this action plan?')}
						onCancel={handleDeleteModalToggle}
						open={isDeleteModalOpen}
						onConfirm={deletePlan}
					></ModalDelete>
				</Row>
			</Col>
		);
	}

	if (!actionPlanId && !isFilling && !hasError) {
		return (
			<Col span={24}>
				<Row justify="center">
					<NewActionPlan onClick={handleToggle} />
				</Row>
			</Col>
		);
	}

	return (
		<S.Content span={24}>
			<Row justify="center" gutter={[0, 30]}>
				<Col span={24}>
					<F.Title />
				</Col>
				<Col span={24}>
					<Row gutter={[25, 0]}>
						<Col span={16}>
							<F.ResponsibleUser />
						</Col>
						<Col span={8}>
							<F.Deadline />
						</Col>
					</Row>
				</Col>
				<Col span={24}>
					<F.Description />
				</Col>
				<Col span={24}>
					<Tasks tasks={tasks} onAdd={handleAddTask} onRemove={handleRemoveTask} />
				</Col>
				<Col span={24}>
					<Footer onClick={handleSubmitActionPlan} isEditing={!!actionPlanId} />
				</Col>
			</Row>
		</S.Content>
	);
}
